import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../utils/api";
import toast from "react-hot-toast";
import {
  Shield,
  ShieldCheck,
  Monitor,
  Activity,
  Copy,
  Check,
  Trash2,
} from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const ACTION_LABELS = {
  "auth.2fa.enable": "Enabled two-factor authentication",
  "auth.2fa.disable": "Disabled two-factor authentication",
  "auth.session.revoke": "Revoked a session",
  "auth.login": "Signed in",
  "auth.logout": "Signed out",
  "merchant.suspend": "Suspended merchant",
};

const formatActionLabel = (action) => {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  if (!action) return "Activity";
  return action
    .split(".")
    .join(" ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US");
};

const truncate = (value, max = 60) => {
  if (!value) return "Unknown device";
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

const SecurityTab = () => {
  // Two-factor authentication state
  const [twoFaLoading, setTwoFaLoading] = useState(true);
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [enableToken, setEnableToken] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [twoFaBusy, setTwoFaBusy] = useState(false);

  // Sessions state
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessions, setSessions] = useState([]);

  // Audit log state
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);

  const fetchTwoFaStatus = async () => {
    try {
      setTwoFaLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/2fa/status`);
      const data = await response.json();
      if (data.success) {
        setTwoFaStatus(data.data);
      }
    } catch (error) {
      toast.error("Failed to load 2FA status");
    } finally {
      setTwoFaLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/sessions`);
      const data = await response.json();
      if (data.success) {
        setSessions(data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load sessions");
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setAuditLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/audit?limit=50`);
      const data = await response.json();
      if (data.success) {
        setAuditLogs(data.data?.logs || []);
      }
    } catch (error) {
      toast.error("Failed to load account activity");
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchTwoFaStatus();
    fetchSessions();
    fetchAuditLogs();
  }, []);

  const handleSetup = async () => {
    try {
      setTwoFaBusy(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/2fa/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        setSetupData(data.data);
      } else {
        toast.error(data.message || "Failed to start 2FA setup");
      }
    } catch (error) {
      toast.error("Failed to start 2FA setup");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const handleEnable = async () => {
    if (!enableToken.trim()) {
      toast.error("Enter the 6-digit code from your authenticator app");
      return;
    }
    try {
      setTwoFaBusy(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/2fa/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: enableToken.trim() }),
      });
      const data = await response.json();
      if (data.success) {
        setBackupCodes(data.data?.backupCodes || []);
        setSetupData(null);
        setEnableToken("");
        toast.success("Two-factor authentication enabled");
        fetchTwoFaStatus();
        fetchAuditLogs();
      } else {
        toast.error(data.message || "Failed to enable 2FA");
      }
    } catch (error) {
      toast.error("Failed to enable 2FA");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const handleDisable = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disable two-factor authentication? This makes your account less secure."
      )
    ) {
      return;
    }
    const token = window.prompt(
      "Enter a 6-digit code from your authenticator app or a backup code to confirm:"
    );
    if (!token || !token.trim()) {
      return;
    }
    try {
      setTwoFaBusy(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/2fa/disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.trim() }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Two-factor authentication disabled");
        setBackupCodes(null);
        fetchTwoFaStatus();
        fetchAuditLogs();
      } else {
        toast.error(data.message || "Failed to disable 2FA");
      }
    } catch (error) {
      toast.error("Failed to disable 2FA");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleRevokeSession = async (id) => {
    if (!window.confirm("Revoke this session? The device will be signed out.")) {
      return;
    }
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/auth/sessions/${id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Session revoked");
        fetchSessions();
        fetchAuditLogs();
      } else {
        toast.error(data.message || "Failed to revoke session");
      }
    } catch (error) {
      toast.error("Failed to revoke session");
    }
  };

  const handleRevokeAll = async () => {
    if (
      !window.confirm(
        "Revoke all other sessions? Every other device will be signed out."
      )
    ) {
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/auth/sessions`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (data.success) {
        const revoked = data.data?.revoked ?? 0;
        toast.success(`Revoked ${revoked} session${revoked === 1 ? "" : "s"}`);
        fetchSessions();
        fetchAuditLogs();
      } else {
        toast.error(data.message || "Failed to revoke sessions");
      }
    } catch (error) {
      toast.error("Failed to revoke sessions");
    }
  };

  const otherSessionsCount = sessions.filter((s) => !s.current).length;

  return (
    <div className="security-tab">
      {/* Two-Factor Authentication */}
      <div className="security-card">
        <div className="security-card-header">
          <Shield size={24} />
          <div>
            <h2>Two-Factor Authentication</h2>
            <p>Add an extra layer of security to your account</p>
          </div>
        </div>

        {twoFaLoading ? (
          <p className="security-muted">Loading...</p>
        ) : backupCodes ? (
          <div className="backup-codes-box">
            <p className="backup-warning">
              Save these backup codes now. They will not be shown again. Each
              code can be used once if you lose access to your authenticator
              app.
            </p>
            <div className="backup-codes-grid">
              {backupCodes.map((code) => (
                <code key={code} className="backup-code">
                  {code}
                </code>
              ))}
            </div>
            <button
              className="security-btn-secondary"
              onClick={() => setBackupCodes(null)}
            >
              I have saved my backup codes
            </button>
          </div>
        ) : twoFaStatus?.enabled ? (
          <div className="security-row">
            <div>
              <span className="status-pill status-enabled">Enabled</span>
              <p className="security-muted">
                {twoFaStatus.backupCodesRemaining} backup code
                {twoFaStatus.backupCodesRemaining === 1 ? "" : "s"} remaining
              </p>
            </div>
            <button
              className="security-btn-danger"
              onClick={handleDisable}
              disabled={twoFaBusy}
            >
              Disable 2FA
            </button>
          </div>
        ) : setupData ? (
          <div className="setup-box">
            <p className="security-muted">
              Add this account to your authenticator app using the secret below,
              then enter the 6-digit code to finish.
            </p>
            <div className="setup-field">
              <label>Secret</label>
              <div className="copy-row">
                <code className="setup-value">{setupData.secret}</code>
                <button
                  className="icon-btn"
                  aria-label="Copy secret"
                  onClick={() => handleCopy(setupData.secret, "secret")}
                >
                  {copiedField === "secret" ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            </div>
            <div className="setup-field">
              <label>Setup URL</label>
              <div className="copy-row">
                <code className="setup-value setup-url">
                  {setupData.otpauthUrl}
                </code>
                <button
                  className="icon-btn"
                  aria-label="Copy setup URL"
                  onClick={() => handleCopy(setupData.otpauthUrl, "otpauth")}
                >
                  {copiedField === "otpauth" ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            </div>
            <div className="setup-field">
              <label>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={enableToken}
                onChange={(e) =>
                  setEnableToken(e.target.value.replace(/[^0-9]/g, ""))
                }
              />
            </div>
            <button
              className="security-btn-primary"
              onClick={handleEnable}
              disabled={twoFaBusy}
            >
              <ShieldCheck size={16} />
              Enable
            </button>
          </div>
        ) : (
          <div className="security-row">
            <p className="security-muted">
              Two-factor authentication is not enabled.
            </p>
            <button
              className="security-btn-primary"
              onClick={handleSetup}
              disabled={twoFaBusy}
            >
              <Shield size={16} />
              Set up 2FA
            </button>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div className="security-card">
        <div className="security-card-header">
          <Monitor size={24} />
          <div>
            <h2>Active Sessions</h2>
            <p>Devices currently signed in to your account</p>
          </div>
          {otherSessionsCount > 0 && (
            <button
              className="security-btn-secondary header-action"
              onClick={handleRevokeAll}
            >
              Revoke all other sessions
            </button>
          )}
        </div>

        {sessionsLoading ? (
          <p className="security-muted">Loading...</p>
        ) : sessions.length === 0 ? (
          <p className="security-muted">No active sessions found.</p>
        ) : (
          <div className="session-list">
            {sessions.map((session) => (
              <div key={session.id} className="session-item">
                <div className="session-info">
                  <div className="session-top">
                    <span className="session-ua">
                      {truncate(session.userAgent)}
                    </span>
                    {session.current && (
                      <span className="status-pill status-current">
                        This device
                      </span>
                    )}
                  </div>
                  <p className="security-muted">
                    {session.ipAddress || "Unknown IP"} - Last active{" "}
                    {formatDateTime(session.lastActiveAt)}
                  </p>
                </div>
                {!session.current && (
                  <button
                    className="icon-btn icon-btn-danger"
                    aria-label="Revoke session"
                    onClick={() => handleRevokeSession(session.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Activity */}
      <div className="security-card">
        <div className="security-card-header">
          <Activity size={24} />
          <div>
            <h2>Account Activity</h2>
            <p>Recent actions on your account</p>
          </div>
        </div>

        {auditLoading ? (
          <p className="security-muted">Loading...</p>
        ) : auditLogs.length === 0 ? (
          <p className="security-muted">No recent activity.</p>
        ) : (
          <div className="audit-list">
            {auditLogs.map((log) => (
              <div key={log.id} className="audit-item">
                <div className="audit-main">
                  <span className="audit-action">
                    {formatActionLabel(log.action)}
                  </span>
                  {log.target_type && (
                    <span className="security-muted">
                      {log.target_type}
                      {log.target_id ? ` #${log.target_id}` : ""}
                    </span>
                  )}
                </div>
                <p className="security-muted audit-meta">
                  {formatDateTime(log.created_at)}
                  {log.ip_address ? ` - ${log.ip_address}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .security-tab {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .security-card {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          box-shadow: var(--sh-sm);
          padding: 22px 24px;
        }
        .security-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
          color: var(--accent);
        }
        .security-card-header h2 {
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
          margin: 0;
        }
        .security-card-header p {
          font-size: 13px;
          color: var(--ink-secondary);
          margin: 2px 0 0;
        }
        .header-action {
          margin-left: auto;
        }
        .security-muted {
          font-size: 13px;
          color: var(--ink-muted);
          margin: 4px 0 0;
        }
        .security-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          border-radius: var(--r-pill);
          font-size: 12px;
          font-weight: 600;
        }
        .status-pill::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
        }
        .status-enabled {
          background: var(--ok-soft);
          color: var(--ok);
        }
        .status-current {
          background: var(--info-soft);
          color: var(--info);
        }
        .security-btn-primary,
        .security-btn-secondary,
        .security-btn-danger {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 16px;
          border-radius: var(--r-sm);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          white-space: nowrap;
        }
        .security-btn-primary {
          background: var(--primary);
          color: #fff;
        }
        .security-btn-primary:hover:not(:disabled) {
          background: var(--primary-hover);
        }
        .security-btn-secondary {
          background: var(--bg-surface);
          color: var(--ink);
          border-color: var(--line-strong);
          box-shadow: var(--sh-sm);
        }
        .security-btn-secondary:hover:not(:disabled) {
          background: var(--bg-inset);
        }
        .security-btn-danger {
          background: var(--bg-surface);
          color: var(--err);
          border-color: var(--line-strong);
          box-shadow: var(--sh-sm);
        }
        .security-btn-danger:hover:not(:disabled) {
          background: var(--err-soft);
          border-color: var(--err);
        }
        .security-btn-primary:disabled,
        .security-btn-secondary:disabled,
        .security-btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--r-sm);
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          color: var(--ink-secondary);
          cursor: pointer;
        }
        .icon-btn:hover {
          background: var(--bg-inset);
        }
        .icon-btn-danger {
          color: var(--err);
        }
        .icon-btn-danger:hover {
          background: var(--err-soft);
        }
        .setup-box,
        .backup-codes-box {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .setup-field {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }
        .setup-field label {
          font-size: 13px;
          font-weight: 600;
          color: var(--ink);
        }
        .setup-field input {
          padding: 10px 12px;
          border-radius: var(--r-sm);
          border: 1px solid var(--line-strong);
          background: var(--bg-surface);
          color: var(--ink);
          font-size: 14px;
          font-family: var(--font-mono);
          max-width: 200px;
          letter-spacing: 4px;
        }
        .setup-field input:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-soft);
        }
        .copy-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .setup-value {
          flex: 1;
          padding: 10px 12px;
          border-radius: var(--r-sm);
          border: 1px solid var(--line);
          background: var(--bg-inset);
          color: var(--ink);
          font-size: 13px;
          font-family: var(--font-mono);
          word-break: break-all;
        }
        .setup-url {
          font-size: 12px;
        }
        .security-btn-primary {
          align-self: flex-start;
        }
        .backup-warning {
          font-size: 13px;
          font-weight: 600;
          color: var(--warn);
          margin: 0;
        }
        .backup-codes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 10px;
        }
        .backup-code {
          padding: 10px 12px;
          border-radius: var(--r-sm);
          border: 1px solid var(--line);
          background: var(--bg-inset);
          color: var(--ink);
          font-size: 14px;
          font-family: var(--font-mono);
          text-align: center;
          letter-spacing: 1px;
        }
        .session-list,
        .audit-list {
          display: flex;
          flex-direction: column;
        }
        .session-item,
        .audit-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 14px 0;
          border-bottom: 1px solid var(--line);
        }
        .session-item:last-child,
        .audit-item:last-child {
          border-bottom: none;
        }
        .session-info {
          min-width: 0;
        }
        .session-top {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .session-ua {
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
        }
        .audit-main {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .audit-action {
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
        }
        .audit-meta {
          white-space: nowrap;
          font-family: var(--font-mono);
        }
        @media (max-width: 576px) {
          .security-card {
            padding: 16px;
          }
          .security-card-header {
            flex-wrap: wrap;
          }
          .header-action {
            margin-left: 0;
            min-height: 44px;
          }
          .security-btn-primary,
          .security-btn-secondary,
          .security-btn-danger {
            min-height: 44px;
          }
          .icon-btn {
            width: 44px;
            height: 44px;
          }
          .session-item,
          .audit-item {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
};

export default SecurityTab;
